import { StateCreator } from "zustand";
import { Answers, Page } from "../../context/AppProvider";
import { chatApi } from "../../api/chatAPI";
import { IPGeolocation, User } from "../../api/models";
import { Brand, Locale } from "../../context/AppProvider";
import { AxiosError } from "axios";

// Define what the slice looks like
export interface AnswerSlice {
  answers: Answers[];
  setAnswer: (answer: Answers[]) => void;
  // The new automation-friendly action
  executeChatRequest: (
    question: string,
    brand: Brand,
    locale: Locale,
    user: User | null,
    ipGeoLocation: IPGeolocation,
  ) => Promise<void>;
}

// Note: Replace 'any' with your actual 'FullStoreState' type if you have one
const createAnswerSlice: StateCreator<any, [], [], AnswerSlice> = (
  set,
  get,
) => ({
  answers: [],
  setAnswer: (answers: Answers[]) => set({ answers }),

  executeChatRequest: async (question, brand, locale, user, ipGeoLocation) => {
    // 1. Grab everything we need from other slices using get()
    const {
      id: conversationID,
      top,
      setIsLoading,
      setError,
      setPage,
      setId: setConversationID,
      chatHistory,
      setChatHistory,
      setLastQuestion,
      setCitation,
    } = get();

    setIsLoading(true);
    setLastQuestion(question);
    setCitation(undefined);
    setPage(Page.CHAT);
    setError({} as AxiosError);

    try {
      // 2. Build history from the current answers in state
      const history = get().answers.map((a: any) => ({
        user: a[0],
        bot: a[1].answer,
        brand: a[2],
      }));

      const request = {
        history: [...history, { user: question, bot: undefined, brand: brand }],
        overrides: {
          top: top,
          semanticRanker: true,
          chatRecord: true,
          brand: brand,
          dummyAnswer: false,
        },
        locale: locale as Locale,
        conversation_id: conversationID,
        ipGeoLocation,
      };

      const result = await chatApi(request);

      // 3. Update the conversation ID and append the answer
      setConversationID(result.conversation_id);

      // Use functional update style within Zustand set
      set((state: any) => ({
        answers: [...state.answers, [question, result, brand]],
      }));

      // 4. ChatHistory sync logic here if needed
      if (chatHistory !== null) {
        const newChatHistory = { ...chatHistory };

        const newConversation = {
          _id: result._id,
          user_id: user?.user_id,
          question: question,
          question_summarization: result.question_summarization,
          answer: result.answer,
          citation_articles: result.citation_articles,
          suggested_articles: result.suggested_articles,
          suggested_videos: result.suggested_videos,
          follow_up_questions: result.follow_up_questions,
          youtube_links: result.youtube_links,
          response_time: result.response_time,
        };

        for (const key in newChatHistory) {
          for (const conversationGroup of newChatHistory[key]) {
            if (conversationGroup._id === result.conversation_id) {
              conversationGroup.conversations.push(newConversation);

              return newChatHistory;
            }
          }
        }
        // If the conversation ID is not found, create a new conversation group
        if (!newChatHistory.today) {
          newChatHistory.today = [];
        }

        newChatHistory.today.unshift({
          _id: result.conversation_id,
          conversations: [newConversation],
        });

        setChatHistory(newChatHistory);
      }
    } catch (e: any) {
      let axiosError = e as AxiosError;
      axiosError.message = "Error: Failed to get an answer.";
      setError(axiosError);
    } finally {
      setIsLoading(false);
    }
  },
});

export default createAnswerSlice;
